import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ChevronLeft,
  FileText,
  Loader2,
  Mail,
  Phone,
  Briefcase,
  Building2,
  Calendar,
  DollarSign,
  User,
  UserCheck,
  Clock,
  CheckCircle2,
  XCircle,
  Send,
  Edit3,
  AlertCircle,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { api } from "../../utils/api";
import { toast } from "sonner";
import { formatSydneyDateLong, formatSydneyDateShort, formatTimestamp } from "../../utils/helpers";
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import AccessRestricted from "../../components/AccessRestricted";

interface OfferDetailData {
  id: number;
  status: "DRAFT" | "SENT" | "ACCEPTED" | "REJECTED" | "EXPIRED";
  salaryOffered: string | number | null;
  startDate: string | null;
  expiryDate: string | null;
  sentAt: string | null;
  acceptedAt: string | null;
  rejectedAt: string | null;
  rejectionReason: string | null;
  supersededAt: string | null;
  candidateSignature: string | null;
  candidateSignedDate: string | null;
  offerLetterContent: string | null;
  createdAt: string;
  updatedAt: string;
  candidate: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    phone: string | null;
    currentPosition: string | null;
  } | null;
  requisition: {
    id: number;
    jobRole: { id: number; roleName: string } | null;
    department: { id: number; departmentName: string } | null;
  } | null;
  offerTemplate: { id: number; name: string } | null;
  requisitionOrganization: { id: number; companyName: string } | null;
  createdBy: { id: number; name: string } | null;
  updatedBy: { id: number; name: string } | null;
  supersededBy: { id: number; name: string } | null;
}

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return "—";
  return formatSydneyDateLong(dateStr) || "—";
};

const formatShortDate = (dateStr: string | null) => {
  if (!dateStr) return "—";
  return formatSydneyDateShort(dateStr) || "—";
};

const formatDateTime = (dateStr: string | null) => {
  if (!dateStr) return "—";
  return formatTimestamp(dateStr) || "—";
};

const getStatusBadgeStyle = (status: string) => {
  switch (status) {
    case "SENT":
      return "bg-blue-50 text-blue-700 border-blue-100";
    case "ACCEPTED":
      return "bg-green-50 text-green-700 border-green-100";
    case "REJECTED":
      return "bg-red-50 text-red-700 border-red-100";
    case "EXPIRED":
      return "bg-gray-100 text-gray-600 border-gray-100";
    default:
      return "bg-orange-50 text-orange-700 border-orange-100";
  }
};

const getStatusLabel = (status: string) => {
  switch (status) {
    case "SENT":
      return "Offer Sent";
    case "ACCEPTED":
      return "Accepted";
    case "REJECTED":
      return "Rejected";
    case "EXPIRED":
      return "Expired";
    default:
      return "Draft";
  }
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case "SENT":
      return <Send size={16} className="text-blue-500" />;
    case "ACCEPTED":
      return <CheckCircle2 size={16} className="text-green-500" />;
    case "REJECTED":
      return <XCircle size={16} className="text-red-500" />;
    case "EXPIRED":
      return <Clock size={16} className="text-gray-500" />;
    default:
      return <Edit3 size={16} className="text-orange-500" />;
  }
};

const OfferDetail: React.FC = () => {
  const ability = useAbility(AbilityContext);
  const navigate = useNavigate();
  const { id } = useParams();

  const canReadOffer = ability.can(ACTIONS.READ, RESOURCES.CANDIDATE_OFFER) || ability.can(ACTIONS.READ, RESOURCES.RECRUITMENT_MODULE);
  if (!canReadOffer) {
    return <AccessRestricted />;
  }

  const [offer, setOffer] = useState<OfferDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOffer = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<{ success: boolean; offer: OfferDetailData }>(`/api/offers/${id}`);
      if (!res.success) throw new Error("Failed to load offer");
      setOffer(res.offer);
    } catch (err: any) {
      console.error("Failed to load offer:", err);
      setError(err.message || "Failed to load offer details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOffer();
  }, [id]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 animate-in fade-in duration-500">
        <Loader2 size={32} className="animate-spin text-gray-400" />
        <p className="mt-3 text-sm font-medium text-gray-500">Loading offer...</p>
      </div>
    );
  }

  if (error || !offer) {
    return (
      <div className="flex flex-col items-center justify-center py-20 animate-in fade-in duration-500">
        <p className="text-sm font-medium text-gray-500">{error || "Offer not found."}</p>
        <div className="flex items-center gap-3 mt-4">
          {error && (
            <Button variant="outline" className="rounded-xl" onClick={fetchOffer}>
              Retry
            </Button>
          )}
          <Button variant="outline" className="rounded-xl" onClick={() => navigate("/recruitment")}>
            Back to Recruitment
          </Button>
        </div>
      </div>
    );
  }

  const candidateName = offer.candidate
    ? `${offer.candidate.firstName} ${offer.candidate.lastName}`
    : "—";

  const candidateUrl = offer.candidate?.id
    ? `/recruitment/candidates/${offer.candidate.id}`
    : null;

  return (
    <div className="flex flex-col space-y-6 animate-in fade-in duration-500 pb-10 px-0 sm:px-4 lg:px-0">
      {/* Back Button */}
      <button
        onClick={() =>
          candidateUrl ? navigate(candidateUrl) : navigate("/recruitment")
        }
        className="flex items-center gap-2 text-gray-500 hover:text-gray-700 transition-colors w-fit"
      >
        <ChevronLeft size={18} />
        <span className="text-sm font-medium">
          {candidateUrl ? `Back to ${candidateName}` : "Back to Recruitment"}
        </span>
      </button>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center text-gray-700 shrink-0">
            <FileText size={28} />
          </div>
          <div className="space-y-1">
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
              Offer Details
            </h1>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge
                variant="outline"
                className={`rounded-lg border-0 font-bold px-2.5 py-1 text-[10px] ${getStatusBadgeStyle(offer.status)}`}
              >
                {getStatusLabel(offer.status)}
              </Badge>
              {offer.status === "ACCEPTED" && (
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-lg gap-1.5 text-green-700 border-green-200 bg-green-50 hover:bg-green-100 hover:text-green-800"
                  onClick={() => navigate("/onboarding")}
                >
                  <UserCheck size={14} />
                  View Onboarding
                </Button>
              )}
              <span className="text-sm text-gray-500 font-medium">
                Created {formatShortDate(offer.createdAt)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Superseded Banner */}
      {offer.supersededAt && offer.supersededBy && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-center gap-3">
          <AlertCircle size={18} className="text-amber-600 shrink-0" />
          <p className="text-sm font-bold text-amber-800">
            This offer was superseded on {formatShortDate(offer.supersededAt)} by{" "}
            {offer.supersededBy.name}
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Offer Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Offer Letter */}
          <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-4">
            <div className="flex items-center gap-2">
              <FileText size={18} className="text-gray-400" />
              <h4 className="font-bold text-gray-900 text-base">Offer Letter</h4>
            </div>
            {offer.offerLetterContent ? (
              <div
                className="text-sm text-gray-700 font-medium prose prose-sm max-w-none border border-gray-100 rounded-xl p-4 bg-gray-50/30 whitespace-pre-wrap"
                dangerouslySetInnerHTML={{ __html: offer.offerLetterContent }}
              />
            ) : (
              <p className="text-sm text-gray-500 font-medium">No offer letter content available.</p>
            )}
          </div>

          {/* Candidate Signature */}
          {offer.candidateSignature && (
            <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-4">
              <div className="flex items-center gap-2">
                <CheckCircle2 size={18} className="text-green-500" />
                <h4 className="font-bold text-gray-900 text-base">Candidate Signature</h4>
              </div>
              <img
                src={offer.candidateSignature}
                alt="Candidate signature"
                className="h-20 object-contain border border-gray-100 rounded-xl p-2 bg-white"
              />
              {offer.candidateSignedDate && (
                <p className="text-xs text-gray-500 font-medium">
                  Signed on {formatDate(offer.candidateSignedDate)}
                </p>
              )}
            </div>
          )}

          {/* Rejection Reason */}
          {offer.rejectionReason && (
            <div className="bg-white p-6 rounded-3xl border border-red-100 shadow-sm space-y-4">
              <div className="flex items-center gap-2">
                <XCircle size={18} className="text-red-500" />
                <h4 className="font-bold text-gray-900 text-base">Rejection Reason</h4>
              </div>
              <p className="text-sm text-gray-700 font-medium whitespace-pre-wrap">
                {offer.rejectionReason}
              </p>
            </div>
          )}
        </div>

        {/* Right Column - Details */}
        <div className="lg:col-span-1 space-y-6">
          {/* Candidate Info */}
          {offer.candidate && (
            <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-5">
              <h4 className="font-bold text-gray-900 text-base">Candidate</h4>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <User size={16} className="text-gray-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">Name</p>
                    <button
                      onClick={() => candidateUrl && navigate(candidateUrl)}
                      className={`text-sm font-bold text-gray-900 mt-0.5 ${candidateUrl ? "hover:text-blue-600 hover:underline" : ""}`}
                    >
                      {candidateName}
                    </button>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Mail size={16} className="text-gray-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">Email</p>
                    <p className="text-sm font-bold text-gray-900 mt-0.5">{offer.candidate.email}</p>
                  </div>
                </div>
                {offer.candidate.phone && (
                  <div className="flex items-start gap-3">
                    <Phone size={16} className="text-gray-400 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">Phone</p>
                      <p className="text-sm font-bold text-gray-900 mt-0.5">{offer.candidate.phone}</p>
                    </div>
                  </div>
                )}
                {offer.candidate.currentPosition && (
                  <div className="flex items-start gap-3">
                    <Briefcase size={16} className="text-gray-400 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">Current Position</p>
                      <p className="text-sm font-bold text-gray-900 mt-0.5">{offer.candidate.currentPosition}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Position Info */}
          {offer.requisition && (
            <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-5">
              <h4 className="font-bold text-gray-900 text-base">Position</h4>
              <div className="space-y-4">
                {offer.requisition.jobRole && (
                  <div className="flex items-start gap-3">
                    <Briefcase size={16} className="text-gray-400 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">Job Role</p>
                      <p className="text-sm font-bold text-gray-900 mt-0.5">{offer.requisition.jobRole.roleName}</p>
                    </div>
                  </div>
                )}
                {offer.requisition.department && (
                  <div className="flex items-start gap-3">
                    <Building2 size={16} className="text-gray-400 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">Department</p>
                      <p className="text-sm font-bold text-gray-900 mt-0.5">{offer.requisition.department.departmentName}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Offer Details */}
          <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-5">
            <h4 className="font-bold text-gray-900 text-base">Offer Details</h4>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <DollarSign size={16} className="text-gray-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">Salary Offered</p>
                  <p className="text-sm font-bold text-gray-900 mt-0.5">
                    {offer.salaryOffered ? `$${Number(offer.salaryOffered).toLocaleString()}` : "—"}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Calendar size={16} className="text-gray-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">Start Date</p>
                  <p className="text-sm font-bold text-gray-900 mt-0.5">{formatDate(offer.startDate)}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Clock size={16} className="text-gray-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">Expiry Date</p>
                  <p className="text-sm font-bold text-gray-900 mt-0.5">{formatDate(offer.expiryDate)}</p>
                </div>
              </div>
              {offer.offerTemplate && (
                <div className="flex items-start gap-3">
                  <FileText size={16} className="text-gray-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">Template</p>
                    <p className="text-sm font-bold text-gray-900 mt-0.5">{offer.offerTemplate.name}</p>
                  </div>
                </div>
              )}
              {offer.requisitionOrganization && (
                <div className="flex items-start gap-3">
                  <Building2 size={16} className="text-gray-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">Organization</p>
                    <p className="text-sm font-bold text-gray-900 mt-0.5">{offer.requisitionOrganization.companyName}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Timeline */}
          <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-4">
            <h4 className="font-bold text-gray-900 text-base">Timeline</h4>
            <div className="space-y-0">
              {[
                {
                  icon: getStatusIcon(offer.status),
                  iconBg: "bg-gray-50",
                  title: `Status: ${getStatusLabel(offer.status)}`,
                  date: formatDateTime(offer.updatedAt),
                },
                {
                  icon: <Calendar size={14} className="text-blue-500" />,
                  iconBg: "bg-blue-50",
                  title: "Offer Created",
                  date: formatDateTime(offer.createdAt),
                },
                ...(offer.sentAt
                  ? [
                      {
                        icon: <Send size={14} className="text-purple-500" />,
                        iconBg: "bg-purple-50",
                        title: "Offer Sent",
                        date: formatDateTime(offer.sentAt),
                      },
                    ]
                  : []),
                ...(offer.acceptedAt
                  ? [
                      {
                        icon: <CheckCircle2 size={14} className="text-green-500" />,
                        iconBg: "bg-green-50",
                        title: "Offer Accepted",
                        date: formatDateTime(offer.acceptedAt),
                      },
                    ]
                  : []),
                ...(offer.rejectedAt
                  ? [
                      {
                        icon: <XCircle size={14} className="text-red-500" />,
                        iconBg: "bg-red-50",
                        title: "Offer Rejected",
                        date: formatDateTime(offer.rejectedAt),
                      },
                    ]
                  : []),
              ].map((event, index, arr) => (
                <div key={index}>
                  <div className="flex items-start gap-3 py-3">
                    <div
                      className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${event.iconBg}`}
                    >
                      {event.icon}
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-900">{event.title}</p>
                      <p className="text-xs text-gray-500 font-medium mt-0.5">{event.date}</p>
                    </div>
                  </div>
                  {index < arr.length - 1 && (
                    <div className="ml-3.5 pl-3.5 border-l border-gray-100 h-4" />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Meta */}
          <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-4">
            <h4 className="font-bold text-gray-900 text-base">Meta</h4>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">Created By</p>
                <p className="text-sm font-bold text-gray-900 mt-0.5">
                  {offer.createdBy?.name ?? "—"}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">Last Updated By</p>
                <p className="text-sm font-bold text-gray-900 mt-0.5">
                  {offer.updatedBy?.name ?? "—"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OfferDetail;
